import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Check, Plane, Zap, Building2, Infinity, Globe } from 'lucide-react'
import { getPlans, createCheckout } from '../lib/api'
import { checkAuth } from '../lib/auth'

const CURRENCY_SYMBOLS = { GBP: '£', USD: '$', CAD: 'C$', EUR: '€', AUD: 'A$', SEK: 'kr', NOK: 'kr', DKK: 'kr', CHF: 'Fr', JPY: '¥', SGD: 'S$', HKD: 'HK$', NZD: 'NZ$' }

export default function Pricing() {
  const [plans, setPlans] = useState([])
  const [currency, setCurrency] = useState('GBP')
  const [country, setCountry] = useState('GB')
  const [loading, setLoading] = useState(true)
  const [billingInterval, setBillingInterval] = useState('month')

  const [plansError, setPlansError] = useState(false)
  const [checkoutId, setCheckoutId] = useState(null)
  const [checkoutError, setCheckoutError] = useState('')

  useEffect(() => {
    getPlans().then(data => {
      setPlans(data.plans); setCurrency(data.currency); setCountry(data.country); setLoading(false)
    }).catch(() => {
      setPlansError(true)
      setLoading(false)
    })
  }, [])

  const formatPrice = (price) => { const symbol = CURRENCY_SYMBOLS[currency] || '£'; return price === 0 ? `${symbol}0` : `${symbol}${(price / 100).toFixed(2)}` }
  const getPrice = (plan) => billingInterval === 'year' ? plan.priceYearly : plan.priceMonthly

  const handleSubscribe = async (plan) => {
    setCheckoutError('')
    setCheckoutId(plan.id)
    try {
      const user = await checkAuth()
      if (!user) { window.location.href = `/login?next=${encodeURIComponent('/pricing')}`; return }
      const { url, error } = await createCheckout(plan.id, billingInterval)
      if (url) window.location.href = url
      else setCheckoutError(error || 'Could not start checkout. Please try again.')
    } catch {
      setCheckoutError('Could not start checkout. Please try again.')
    } finally {
      setCheckoutId(null)
    }
  }

  if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><div className="w-8 h-8 border-2 border-flight-cyan border-t-transparent rounded-full animate-spin" /></div>

  return (
    <div className="min-h-screen bg-slate-950">
      <nav className="border-b border-slate-800/50 bg-slate-950/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><Plane className="w-5 h-5 text-flight-cyan" /><span className="text-lg font-bold text-white">FlightWall</span></Link>
          <Link to="/" className="text-slate-400 hover:text-white text-sm">← Back</Link>
        </div>
      </nav>
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-flight-cyan/10 border border-flight-cyan/20 text-flight-cyan text-xs font-medium mb-4"><Globe className="w-3 h-3" />Prices in {currency} for {country}</div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Simple, Transparent Pricing</h1>
          <div className="flex items-center justify-center gap-2 mt-6">
            <button onClick={() => setBillingInterval('month')} className={`px-4 py-2 rounded-lg text-sm font-medium ${billingInterval === 'month' ? 'bg-flight-cyan text-slate-950' : 'bg-slate-800 text-slate-300'}`}>Monthly</button>
            <button onClick={() => setBillingInterval('year')} className={`px-4 py-2 rounded-lg text-sm font-medium ${billingInterval === 'year' ? 'bg-flight-cyan text-slate-950' : 'bg-slate-800 text-slate-300'}`}>Yearly <span className="text-xs opacity-70">(save 17%)</span></button>
          </div>
        </div>
        {plansError && <div className="text-center text-red-400 text-sm mb-6">Couldn't load pricing right now. Please try again shortly.</div>}
        {checkoutError && <div className="text-center text-red-400 text-sm mb-6">{checkoutError}</div>}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map(plan => (
            <div key={plan.id} className={`relative rounded-2xl border p-6 flex flex-col ${plan.name === 'Pro' ? 'border-flight-cyan/50 bg-flight-cyan/5' : 'border-slate-800 bg-slate-900/50'}`}>
              {plan.name === 'Pro' && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-flight-cyan text-slate-950 text-xs font-bold">Most Popular</div>}
              <div className="flex items-center gap-2 text-slate-400 mb-4">
                {plan.name === 'Free' && <Plane className="w-5 h-5" />}{plan.name === 'Pro' && <Zap className="w-5 h-5" />}{plan.name === 'Business' && <Building2 className="w-5 h-5" />}{plan.name === 'Enterprise' && <Infinity className="w-5 h-5" />}
                <span className="text-sm font-medium">{plan.name}</span>
              </div>
              <div className="mb-4"><span className="text-4xl font-bold text-white">{formatPrice(getPrice(plan))}</span><span className="text-slate-500">/{billingInterval === 'year' ? 'yr' : 'mo'}</span></div>
              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((f, i) => <li key={i} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /><span className="text-slate-300">{f}</span></li>)}
              </ul>
              {plan.name === 'Free' ? (
                <Link to="/admin" className="w-full py-3 rounded-xl text-center font-medium border border-slate-700 text-white hover:bg-slate-800">Get Started</Link>
              ) : (
                <button
                  onClick={() => handleSubscribe(plan)}
                  disabled={checkoutId === plan.id}
                  className={`w-full py-3 rounded-xl text-center font-medium disabled:opacity-50 ${plan.name === 'Pro' ? 'bg-flight-cyan text-slate-950' : 'border border-slate-700 text-white hover:bg-slate-800'}`}
                >
                  {checkoutId === plan.id ? 'Loading…' : `Start ${plan.name} Trial`}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
